import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { map } from 'rxjs';
import { CiaPart } from '@features/intervention/assessment/cia/cia-form-groups';
import { NewRequestFormComponent } from '../../../new-request/components/new-request-form/new-request-form.component';
import { InterventionAffiliateAssignmentComponent } from '../../../referral/components/intervention-affiliate-assignment/intervention-affiliate-assignment.component';
import { TreatmentGoalsPanelComponent } from '../../../treatment-plan/components/treatment-goals-panel/treatment-goals-panel.component';
import { ProviderReferralPanelComponent } from '../../../referral/components/provider-referral-panel/provider-referral-panel.component';
import { ProviderReferralSummaryComponent } from '../../../referral/components/provider-referral-summary/provider-referral-summary.component';
import { InterventionTaskPanelComponent } from '../../../delivery/components/intervention-task-panel/intervention-task-panel.component';
import { InterventionConsentsPanelComponent } from '../../../consent-and-preferences/components/intervention-consents-panel/intervention-consents-panel.component';
import { InterventionReportsPanelComponent } from '../../../delivery/components/intervention-reports-panel/intervention-reports-panel.component';
import { InterventionSessionsPanelComponent } from '../../../delivery/components/intervention-sessions-panel/intervention-sessions-panel.component';
import { CiaHostComponent } from '../../../assessment/cia/components/cia-host/cia-host.component';
import { RiskScreenerPanelComponent } from '../../../assessment/risk-screening/components/risk-screener-panel/risk-screener-panel.component';
import { SHARED_IMPORTS } from '../../../../../shared/modules/shared-imports.module';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { HistoryListComponent } from '@shared/components/history-list/history-list.component';
import { Permission } from '@shared/enums/permission.enum';
import { PermissionService } from '@core/services/permission.service';
import { PresentingProblemPanelComponent } from '../../../presenting-problem/components/presenting-problem-panel/presenting-problem-panel.component';
import { InterventionReferrerPanelComponent } from '../../../new-request/components/intervention-referrer-panel/intervention-referrer-panel.component';
import { InterventionEligibilityPanelComponent } from '../../../eligibility/components/intervention-eligibility-panel/intervention-eligibility-panel.component';
import { ReferralPreferencePanelComponent } from '../../../consent-and-preferences/components/referral-preference-panel/referral-preference-panel.component';
import { InterventionStagePanelComponent } from '../../components/intervention-stage-panel/intervention-stage-panel.component';
import { WizardSidePanelsComponent } from '../../components/wizard-side-panels/wizard-side-panels.component';
import { WizardStepsNavComponent } from '../../components/wizard-steps-nav/wizard-steps-nav.component';
import { InterventionStore } from '../../../intervention.store';
import { viewForTask } from '../../../intervention-next-task';
import {
  InterventionProgressStep,
  InterventionResponse,
  InterventionTaskResponse,
  UpdateInterventionRequest,
} from '../../../intervention.model';

// The steps of the wizard that are parts of the clinical assessment (the CIA), and the part each one shows.
const ASSESSMENT_PART_OF_STEP: Record<string, CiaPart> = {
  PresentingProblem: 'presenting',
  ClinicalAssessment: 'clinical',
  FunctionalImpactAndRisk: 'functional',
  TreatmentPlanning: 'treatment',
};

const STEP_OF_ASSESSMENT_PART: Record<CiaPart, string> = {
  presenting: 'PresentingProblem',
  clinical: 'ClinicalAssessment',
  functional: 'FunctionalImpactAndRisk',
  treatment: 'TreatmentPlanning',
};

// The work of the case that comes after the wizard. The history is not one of them: it is opened from the audit trail beside the steps.
const SECTIONS = ['tasks', 'sessions', 'reports'] as const;
const SECTION_LABELS: Record<(typeof SECTIONS)[number], string> = { tasks: 'Tasks', sessions: 'Sessions', reports: 'Reports' };

@Component({
  selector: 'app-intervention-detail-page',
  imports: [
    RouterLink,
    NewRequestFormComponent,
    InterventionStagePanelComponent,
    InterventionAffiliateAssignmentComponent,
    ProviderReferralPanelComponent,
    ProviderReferralSummaryComponent,
    TreatmentGoalsPanelComponent,
    ReferralPreferencePanelComponent,
    InterventionReferrerPanelComponent,
    PresentingProblemPanelComponent,
    InterventionEligibilityPanelComponent,
    InterventionTaskPanelComponent,
    CiaHostComponent,
    RiskScreenerPanelComponent,
    InterventionSessionsPanelComponent,
    InterventionReportsPanelComponent,
    InterventionConsentsPanelComponent,
    HistoryListComponent,
    WizardSidePanelsComponent,
    WizardStepsNavComponent,
    ...SharedMaterialUIModule,
    ...SHARED_IMPORTS,
  ],
  templateUrl: './intervention-detail.page.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class InterventionDetailPage {
  private readonly route = inject(ActivatedRoute);
  private readonly permissionService = inject(PermissionService);
  protected readonly store = inject(InterventionStore);
  // The case in the address. The page is reused when only the id changes (a link from one case to another), so it follows the address.
  private readonly caseId = toSignal(this.route.paramMap.pipe(map((params) => params.get('id') ?? '')), {
    initialValue: this.route.snapshot.paramMap.get('id') ?? '',
  });

  protected get id(): string {
    return this.caseId();
  }

  // The clinical assessment, which stays on screen while the case moves between its parts.
  private readonly ciaHost = viewChild(CiaHostComponent);

  // The steps that wait for eligibility: the work after it (the assessment and what follows) cannot be started before the member is found eligible.
  protected readonly lockedSteps = computed<string[]>(() => {
    const intervention = this.store.selected();
    if (!intervention || !this.progressKnown() || intervention.eligibilityStatus === 'Eligible') {
      return [];
    }

    const keys = this.steps().map((step) => step.key);
    return keys.slice(keys.indexOf('Eligibility') + 1);
  });

  // The steps of this case, in the order of the wizard. The server works out which apply to the type and how far each has got; a portal that is
  // not told (it only sees the case) gets the steps the type has, not started.
  protected readonly steps = computed<InterventionProgressStep[]>(() => {
    const fromServer = this.store.progress()?.steps ?? [];
    return fromServer.length > 0 ? fromServer.filter((step) => step.state !== 'NotApplicable') : this.defaultSteps();
  });

  // The sections after the wizard; the sessions and reports are the care after the referral, so they only show for the types that have a Service
  // Delivery stage, and once the case has reached it.
  protected readonly sections = computed(() =>
    SECTIONS.filter((section) => (section === 'sessions' || section === 'reports' ? this.reachedServiceDelivery() : true)).map((key) => ({
      key,
      label: SECTION_LABELS[key],
    })),
  );

  // The step or section the user chose; until they do, the case stays where it opened (it does not jump while they work).
  private readonly chosenView = signal<string | null>(null);

  // The step or section that is open, and whether the case has found the one it opens at (it is not drawn before, so it does not flash the first step).
  protected readonly view = computed(() => this.chosenView() ?? '');
  protected readonly ready = computed(() => this.chosenView() !== null);

  // Opens the case at the first step that is not done, or when every step is done at the step or section of the next task.
  private readonly landingView = computed(() => {
    const next = this.store.progress()?.nextStepKey;
    return this.resolveView(next ?? this.taskView() ?? this.viewOfStage(this.store.selected()?.status));
  });

  // The part of the assessment that is open, when the open step is one of its parts and the type takes an assessment.
  protected readonly ciaPart = computed<CiaPart | null>(() => {
    const intervention = this.store.selected();
    return intervention && this.isCiaEligible(intervention) ? (ASSESSMENT_PART_OF_STEP[this.view()] ?? null) : null;
  });

  // Where the open step stands among the steps, for the buttons under it (a section has none).
  private readonly stepIndex = computed(() => this.steps().findIndex((step) => step.key === this.view()));

  // Whether the server told this portal how far the case has got (only internal staff work the wizard; a provider is not told).
  protected readonly progressKnown = computed(() => (this.store.progress()?.steps.length ?? 0) > 0);

  // A portal that is not told how far the case has got, once it is known not to be (a provider): it is told the referral, not the wizard.
  protected readonly isProvider = computed(() => this.store.progress() !== null && !this.progressKnown());

  protected readonly stepNumber = computed(() => (this.progressKnown() && this.stepIndex() >= 0 ? this.stepIndex() + 1 : null));
  protected readonly previousStep = computed(() => this.steps()[this.stepIndex() - 1] ?? null);
  protected readonly nextStep = computed(() => (this.stepIndex() >= 0 ? (this.steps()[this.stepIndex() + 1] ?? null) : null));

  // The next task this user can do: the first pending task of the current stage, in the task order, that the
  // user's permissions allow them to act on. Its step or section is marked so they know where to go next.
  protected readonly nextTask = computed<InterventionTaskResponse | null>(() => {
    const status = this.store.selected()?.status;
    if (!status || status === 'Closed') {
      return null;
    }

    return (
      this.store
        .tasks()
        .filter((task) => task.stage === status && task.status === 'Pending' && this.canActOn(task))
        .sort((a, b) => a.displayOrder - b.displayOrder)[0] ?? null
    );
  });

  protected readonly taskView = computed(() => {
    const task = this.nextTask();
    return task ? this.resolveView(viewForTask(task)) : null;
  });

  // The case status on the latest close-out report that was not returned, offered when the intervention is closed.
  protected readonly suggestedClosureOutcomeId = computed(
    () =>
      this.store
        .reports()
        .filter((report) => report.reportType === 'CloseOut' && report.status !== 'Returned')
        .sort((a, b) => b.submittedDate.localeCompare(a.submittedDate))[0]?.closureOutcomeId ?? null,
  );

  constructor() {
    // Opens the case at its first step that is not done, once the case and its progress are known.
    effect(() => {
      if (this.store.selected() && this.store.progress()) {
        const landing = this.landingView();
        untracked(() => {
          if (this.chosenView() === null) {
            this.chosenView.set(landing);
          }
        });
      }
    });

    this.store.loadInterventionTypes();

    // Opens a case, and opens the next one afresh: what the case before it held is forgotten, so it does not show a step or data of another case.
    effect(() => {
      const id = this.caseId();
      untracked(() => this.openCase(id));
    });
  }

  private openCase(id: string): void {
    this.chosenView.set(null);
    this.store.resetCase();

    if (id) {
      this.store.loadOne(id);
      this.store.loadHistory(id);
      // Loaded here, not by the Consent step, because the Referral and Sessions views depend on it too.
      this.store.loadConsents(id);
      this.store.loadReports(id);
    }
  }

  // Opens a step or section. Leaving a part of the assessment saves what was entered in it first (a draft); when that cannot be done the case
  // stays, at the part that needs attention.
  protected async open(key: string): Promise<void> {
    if (key === this.view() || this.lockedSteps().includes(key)) {
      return;
    }

    const host = this.ciaHost();
    if (host && this.ciaPart()) {
      const outcome = await host.saveIfChanged();

      if (outcome === 'failed') {
        return;
      }

      if (outcome !== 'saved') {
        this.chosenView.set(STEP_OF_ASSESSMENT_PART[outcome]);
        host.showFirstInvalidField();
        return;
      }
    }

    this.chosenView.set(key);
    window.scrollTo({ top: 0 });
  }

  // The assessment was saved, locked or reopened: the case reads its status, progress and history again.
  protected onCiaChanged(): void {
    if (this.id) {
      this.store.loadOne(this.id);
      this.store.loadHistory(this.id);
    }
  }

  // A view that the type does not have is replaced by the one that holds its work: the risk screener is part of the presenting problem for the
  // types without an assessment. Anything else that is not there goes to the first step.
  private resolveView(view: string): string {
    if (view === 'history' || this.sections().some((section) => section.key === view) || this.steps().some((step) => step.key === view)) {
      return view;
    }

    return view === 'FunctionalImpactAndRisk' ? 'PresentingProblem' : (this.steps()[0]?.key ?? 'NewRequest');
  }

  // Where the work of a stage is when no step is left in it: the sections after the wizard for the later stages.
  private viewOfStage(status: string | undefined): string {
    switch (status) {
      case 'Referral':
        return 'ReferralMatching';
      case 'ServiceDelivery':
        return 'sessions';
      case 'Reporting':
      case 'Approval':
        return 'reports';
      case 'Closed':
        return 'history';
      default:
        return 'NewRequest';
    }
  }

  // The steps a type has when the server did not say (a portal that only sees the case). A provider has one: the referral it answers, and
  // once it has accepted it, what it has been told of the case.
  private defaultSteps(): InterventionProgressStep[] {
    const intervention = this.store.selected();
    if (!intervention) {
      return [];
    }

    if (this.isProvider()) {
      return [{ key: 'ReferralMatching', label: 'Referral', state: 'NotStarted' }];
    }

    const keys = ['NewRequest', 'Eligibility', 'PresentingProblem'];
    if (this.isCiaEligible(intervention)) {
      keys.push('ClinicalAssessment', 'FunctionalImpactAndRisk');
    }

    if (this.isCiaEligible(intervention) || intervention.usesTreatmentGoals) {
      keys.push('TreatmentPlanning');
    }

    if (this.hasReferralModel(intervention)) {
      keys.push('ConsentAndPreferences', 'ReferralMatching');
    }

    return keys.map((key) => ({ key, label: STEP_LABELS[key] ?? key, state: 'NotStarted' }));
  }

  // Staff do almost everything; a provider only records service consent, sessions and attendance.
  private canActOn(task: InterventionTaskResponse): boolean {
    switch (task.completesOnEvent) {
      case 'ServiceConsentGranted':
        return this.permissionService.hasPermission(Permission.ConsentsRecord);
      case 'RiskScreenerCompleted':
        return this.permissionService.hasPermission(Permission.RiskScreenerCreate);
      case 'AttendanceCaptured':
      case 'CloseOutReportSubmitted':
      case 'OutcomeReportSubmitted':
        return this.permissionService.hasPermission(Permission.SessionsRecord);
      default:
        return this.permissionService.hasPermission(Permission.InterventionsUpdate);
    }
  }

  // The case is at or past the Service Delivery stage of its type (Financial and Legal have none).
  private reachedServiceDelivery(): boolean {
    const stages = this.store.lifecycleStages();
    const status = this.store.selected()?.status;
    return !!status && stages.includes('ServiceDelivery') && stages.indexOf(status) >= stages.indexOf('ServiceDelivery');
  }

  // The clinical case notes and the referral summary they build on only apply to the counselling types.
  protected usesSessionNotes(intervention: InterventionResponse): boolean {
    return !!this.store.interventionTypes().find((type) => type.id === intervention.interventionTypeId)?.usesSessionNotes;
  }

  protected canAssignAffiliate(): boolean {
    return this.permissionService.hasPermission(Permission.InterventionsUpdate);
  }

  protected canUpdate(): boolean {
    return this.permissionService.hasPermission(Permission.InterventionsUpdate);
  }

  protected onAffiliateAssigned(): void {
    if (this.id) this.store.loadHistory(this.id);
  }

  // The old direct-assign widget bypasses the referral/pool/accept-decline workflow entirely, so it
  // only makes sense for intervention types that predate (or fall outside) the Referral Framework.
  protected hasReferralModel(intervention: InterventionResponse): boolean {
    return !!this.store
      .interventionTypes()
      .find((type) => type.id === intervention.interventionTypeId)?.referralModel;
  }

  // Which types take a CIA is configured on the server (InterventionTypeAssessmentType), not hardcoded here.
  protected isCiaEligible(intervention: InterventionResponse): boolean {
    return !!this.store
      .interventionTypes()
      .find((type) => type.id === intervention.interventionTypeId)
      ?.assessmentTypeCodes.includes('CIA');
  }

  protected isRiskScreenerEligible(intervention: InterventionResponse): boolean {
    return !!this.store
      .interventionTypes()
      .find((type) => type.id === intervention.interventionTypeId)
      ?.assessmentTypeCodes.includes('RISK_SCREENER');
  }

  protected saveIntervention(request: UpdateInterventionRequest): void {
    if (!this.id || !this.canUpdate()) {
      return;
    }

    this.store.update(this.id, request);
  }
}

// The names of the steps, for a portal that is not told them.
const STEP_LABELS: Record<string, string> = {
  NewRequest: 'New request',
  Eligibility: 'Eligibility',
  PresentingProblem: 'Presenting problem',
  ClinicalAssessment: 'Clinical assessment',
  FunctionalImpactAndRisk: 'Functional impact and risk',
  TreatmentPlanning: 'Treatment planning',
  ConsentAndPreferences: 'Referral preferences and consent',
  ReferralMatching: 'Referral matching',
};
